import type { ApolloClient } from "@apollo/client";

import {
  AbortScoutTurnDocument,
  GetScoutConversationHistoryDocument,
  SendScoutMessageDocument,
  type ScoutConversationMessage,
} from "generated-graphql/graphql";

import type { StreamCache } from "../../../../stream/cache";
import type { ScoutActiveTurn } from "./types";

import { StreamObserver } from "../../../../stream/observer";
import {
  scoutConversationStore,
  type ScoutConversationSnapshot,
  type ScoutConversationStore,
} from "./scoutConversationStore";

type ControllerOptions = {
  apolloClient: ApolloClient<object>;
  streamClient: StreamCache;
  store?: ScoutConversationStore;
  getIdTokenRefreshIfExpired: () => Promise<string>;
};

type AttachConversationInput = {
  tenantId: string | undefined;
  conversationId: string | undefined;
};

type SendMessageInput = {
  tenantId: string;
  conversationId: string | undefined;
  content: string;
};

type AbortTurnInput = {
  tenantId: string;
  conversationId: string;
};

type RefetchConversationInput = {
  tenantId: string;
  conversationId: string;
};

type LoadHistoryReason =
  | "attach-initial"
  | "abort-recovery"
  | "manual-refetch"
  | "terminal-refetch"
  | "closed-active-refetch";

type EnsureStreamReason =
  | "attach-active-turn"
  | "send-message"
  | "abort-turn"
  | "load-history-active-turn"
  | "history-resume";

type ConversationRuntimeSnapshot = {
  loading: boolean;
  error: Error | undefined;
  isSending: boolean;
  isAborting: boolean;
};

// Controller-owned runtime state for one conversation. This is intentionally
// separate from ScoutConversationStore: the store holds renderable chat data,
// while the controller holds ephemeral transport/query bookkeeping.
type ConversationRuntime = ConversationRuntimeSnapshot & {
  tenantId: string;
  // Number of mounted hook consumers currently interested in this conversation.
  attachmentCount: number;
  // Monotonic guard so stale history responses cannot overwrite newer loads.
  historyLoadVersion: number;
  observer: StreamObserver | undefined;
  observerUnsubscribe: (() => void) | undefined;
  // Cursor used when attaching or re-attaching the SSE stream.
  initialLastEventId: string | undefined;
  // Prevents duplicate application of replayed SSE events after reconnects.
  processedEventIds: Set<string>;
  // Cursor derived from persisted history for "resume from latest known event".
  historyResumeEventId: string | undefined;
  // Prevents reconnect thrash when a stream closes during an active turn.
  lastRestartKey: string | undefined;
  // Prevents repeated final-history refetches for the same terminal event.
  lastTerminalRefetchKey: string | undefined;
  // Prevents repeated recovery refetches when the stream closes multiple times
  // for the same active turn without ever delivering a terminal event.
  lastClosedActiveRefetchKey: string | undefined;
  // Distinguishes a real post-connect close from the observer's bootstrap
  // "closed" snapshot before the network connection has started.
  hasStartedStreamConnection: boolean;
  // Coalesces bursty observer notifications into one controller flush window.
  pendingStreamUpdateTimeout: ReturnType<typeof setTimeout> | undefined;
};

export type ScoutConversationControllerSnapshot = {
  storeSnapshot: ScoutConversationSnapshot;
  runtimesByConversationId: Record<string, ConversationRuntimeSnapshot>;
  // When a new conversation starts optimistically, the hook may still be
  // rendering the optimistic id while the controller has already reconciled to
  // the persisted id. This alias map bridges that short handoff window.
  reconciledConversationIdsByConversationId: Record<string, string>;
};

type SendMessageRequest = {
  optimisticConversationId: string;
  completion: Promise<string | undefined>;
};

const EMPTY_CONVERSATION_RUNTIME_SNAPSHOT: ConversationRuntimeSnapshot = {
  loading: false,
  error: undefined,
  isSending: false,
  isAborting: false,
};

const EMPTY_CONTROLLER_SNAPSHOT: ScoutConversationControllerSnapshot = {
  storeSnapshot: {
    conversationsById: {},
    messagesByConversationId: {},
    activeTurnsByConversationId: {},
    streamStatusByConversationId: {},
  },
  runtimesByConversationId: {},
  reconciledConversationIdsByConversationId: {},
};

function getTimestampMs(value: unknown): number | undefined {
  if (typeof value !== "string" && typeof value !== "number" && !(value instanceof Date)) {
    return undefined;
  }

  const timestampMs = new Date(value).getTime();
  return Number.isFinite(timestampMs) ? timestampMs : undefined;
}

function getHistoryResumeEventId(messages: ScoutConversationMessage[]) {
  const timestampMs = messages.reduce<number | undefined>((latestTimestampMs, message) => {
    const messageTimestampMs =
      getTimestampMs(message.updatedAt) ?? getTimestampMs(message.createdAt);
    const partTimestampMs = message.parts.reduce<number | undefined>(
      (latestPartTimestampMs, part) => {
        const timestamp = getTimestampMs(part.createdAt);
        if (timestamp === undefined) {
          return latestPartTimestampMs;
        }

        return latestPartTimestampMs === undefined
          ? timestamp
          : Math.max(latestPartTimestampMs, timestamp);
      },
      undefined
    );
    let nextTimestampMs: number | undefined;
    if (messageTimestampMs === undefined) {
      nextTimestampMs = partTimestampMs;
    } else if (partTimestampMs === undefined) {
      nextTimestampMs = messageTimestampMs;
    } else {
      nextTimestampMs = Math.max(messageTimestampMs, partTimestampMs);
    }

    if (nextTimestampMs === undefined) {
      return latestTimestampMs;
    }

    return latestTimestampMs === undefined
      ? nextTimestampMs
      : Math.max(latestTimestampMs, nextTimestampMs);
  }, undefined);

  return timestampMs === undefined ? undefined : `${timestampMs}-0`;
}

function shouldResumeHistoryStream(messages: ScoutConversationMessage[]) {
  const lastMessage = messages.at(-1);
  if (!lastMessage) {
    return true;
  }

  return lastMessage.role === "user";
}

function isTerminalTurn(turn: ScoutActiveTurn | undefined) {
  return turn?.status === "finished" || turn?.status === "aborted" || turn?.status === "error";
}

function isLocallyActiveTurn(turn: ScoutActiveTurn | undefined): turn is ScoutActiveTurn {
  return turn?.status === "starting" || turn?.status === "streaming";
}

function normalizeError(error: unknown): Error | undefined {
  if (!error) {
    return undefined;
  }

  return error instanceof Error ? error : new Error(String(error));
}

function getScoutStreamUrl(tenantId: string | undefined, conversationId: string | undefined) {
  if (!tenantId || !conversationId) {
    return "/events/scout/disabled";
  }

  return `/events/${tenantId}/scout/${conversationId}`;
}

function createOptimisticId(kind: string) {
  return `optimistic-scout-${kind}-${crypto.randomUUID()}`;
}

function isOptimisticId(id: string | undefined) {
  return id?.startsWith("optimistic-scout-") ?? false;
}

function shouldDebugScoutConversation() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem("scout:debug") === "1" ||
    new URLSearchParams(window.location.search).get("scoutDebug") === "1"
  );
}

function debugScoutConversation(label: string, details: Record<string, unknown>) {
  if (!shouldDebugScoutConversation()) {
    return;
  }

  console.debug(`[ScoutConversation] ${label}`, details);
}

function hasHydratedConversationMessages(
  snapshot: ScoutConversationSnapshot,
  conversationId: string
) {
  return conversationId in snapshot.messagesByConversationId;
}

/**
 * Owns Scout's non-React runtime behavior.
 *
 * Responsibilities:
 * - load/refetch conversation history
 * - orchestrate optimistic send/abort flows
 * - attach SSE observers and apply live events into the store
 * - keep per-conversation transport/query bookkeeping out of React state
 *
 * Non-responsibilities:
 * - render-facing chat state; that lives in ScoutConversationStore
 * - route/display selection; the hook still decides which conversation the UI
 *   is currently showing
 */
export class ScoutConversationController {
  private readonly apolloClient: ApolloClient<object>;
  private readonly streamClient: StreamCache;
  private readonly store: ScoutConversationStore;
  private readonly getIdTokenRefreshIfExpired: () => Promise<string>;
  private readonly listeners = new Set<() => void>();
  private readonly runtimeByConversationId = new Map<string, ConversationRuntime>();
  private snapshot: ScoutConversationControllerSnapshot;

  public constructor(options: ControllerOptions) {
    this.apolloClient = options.apolloClient;
    this.streamClient = options.streamClient;
    this.store = options.store ?? scoutConversationStore;
    this.getIdTokenRefreshIfExpired = options.getIdTokenRefreshIfExpired;
    this.snapshot = {
      ...EMPTY_CONTROLLER_SNAPSHOT,
      storeSnapshot: this.store.getSnapshot(),
    };
    this.store.subscribe(this.handleStoreUpdate);
  }

  public subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  public getSnapshot = () => this.snapshot;

  // Registers interest in a conversation. Multiple mounted consumers can attach
  // to the same conversation without fighting over a singleton runtime.
  public attachConversation(input: AttachConversationInput) {
    if (!input.tenantId || !input.conversationId) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    runtime.attachmentCount += 1;
    const snapshot = this.store.getSnapshot();
    const activeTurn = snapshot.activeTurnsByConversationId[input.conversationId];

    debugScoutConversation("attach conversation", {
      conversationId: input.conversationId,
      attachmentCount: runtime.attachmentCount,
      activeTurnStatus: activeTurn?.status,
      hasObserver: Boolean(runtime.observer),
      hasHydratedMessages: hasHydratedConversationMessages(snapshot, input.conversationId),
    });

    if (isOptimisticId(input.conversationId)) {
      return;
    }

    if (isLocallyActiveTurn(activeTurn)) {
      this.ensureStreamAttached({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        initialLastEventId: activeTurn.lastEventId ?? runtime.historyResumeEventId,
        reason: "attach-active-turn",
      });
      return;
    }

    if (
      runtime.attachmentCount === 1 &&
      !runtime.loading &&
      !hasHydratedConversationMessages(snapshot, input.conversationId)
    ) {
      void this.loadHistory({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        forceNetwork: false,
        reason: "attach-initial",
      });
    }
  }

  // Releases one consumer's interest in a conversation. The runtime stays alive
  // if another consumer is attached or if a turn is still actively streaming.
  public detachConversation(conversationId: string | undefined) {
    if (!conversationId) {
      return;
    }

    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    runtime.attachmentCount = Math.max(0, runtime.attachmentCount - 1);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.detachStream(conversationId);
      this.maybeDeleteRuntime(conversationId);
    }
  }

  public detachAll() {
    const conversationIds = [...this.runtimeByConversationId.keys()];
    for (const conversationId of conversationIds) {
      this.detachStream(conversationId);
      this.runtimeByConversationId.delete(conversationId);
    }

    this.setControllerSnapshot({
      ...EMPTY_CONTROLLER_SNAPSHOT,
      storeSnapshot: this.store.getSnapshot(),
    });
  }

  // Starts a new turn for the given conversation (or a new optimistic
  // conversation) and returns both the optimistic id and a promise for the
  // reconciled/persisted conversation id.
  public sendMessage(input: SendMessageInput): SendMessageRequest {
    const trimmedContent = input.content.trim();
    if (trimmedContent.length === 0) {
      return {
        optimisticConversationId: input.conversationId ?? createOptimisticId("conversation"),
        completion: Promise.resolve(undefined),
      };
    }

    let targetConversationId = input.conversationId;
    if (targetConversationId !== undefined && isOptimisticId(targetConversationId)) {
      targetConversationId =
        this.snapshot.reconciledConversationIdsByConversationId[targetConversationId];
    }
    const optimisticConversationId =
      targetConversationId ?? input.conversationId ?? createOptimisticId("conversation");
    const optimisticUserMessageId = createOptimisticId("message");
    const optimisticRequestId = createOptimisticId("request");
    const runtime = this.ensureRuntime(optimisticConversationId, input.tenantId);
    const activeTurn = targetConversationId
      ? this.store.getSnapshot().activeTurnsByConversationId[targetConversationId]
      : undefined;
    const targetRuntime = targetConversationId
      ? this.runtimeByConversationId.get(targetConversationId)
      : undefined;
    const historyResumeEventId = targetRuntime?.historyResumeEventId;
    const targetStreamInitialLastEventId = targetConversationId
      ? (activeTurn?.lastEventId ?? historyResumeEventId)
      : undefined;

    this.setRuntimeSnapshot(optimisticConversationId, {
      isSending: true,
      isAborting: false,
      loading: false,
      error: undefined,
    });
    this.store.startOptimisticTurn({
      tenantId: input.tenantId,
      conversationId: optimisticConversationId,
      userMessageId: optimisticUserMessageId,
      requestId: optimisticRequestId,
      content: trimmedContent,
    });

    const completion = this.apolloClient
      .mutate({
        mutation: SendScoutMessageDocument,
        variables: {
          input: {
            tenantId: input.tenantId,
            conversationId: targetConversationId,
            content: trimmedContent,
          },
        },
      })
      .then((result) => {
        const sendResult = result.data?.sendMessage;
        if (!sendResult) {
          throw new Error("Scout did not return a send result.");
        }

        this.store.reconcileOptimisticTurn({
          tenantId: input.tenantId,
          optimisticConversationId,
          conversationId: sendResult.conversationId,
          optimisticUserMessageId,
          userMessageId: sendResult.messageId,
          optimisticRequestId,
          requestId: sendResult.requestId,
          content: trimmedContent,
          title: sendResult.title,
        });

        if (optimisticConversationId !== sendResult.conversationId) {
          this.transferRuntime({
            fromConversationId: optimisticConversationId,
            toConversationId: sendResult.conversationId,
            tenantId: input.tenantId,
          });
        }

        this.setRuntimeSnapshot(sendResult.conversationId, {
          isSending: false,
          error: undefined,
        });
        this.ensureStreamAttached({
          tenantId: input.tenantId,
          conversationId: sendResult.conversationId,
          initialLastEventId: targetStreamInitialLastEventId,
          reason: "send-message",
        });
        this.maybeDeleteRuntime(optimisticConversationId);

        return sendResult.conversationId;
      })
      .catch((error) => {
        this.store.markTurnError({
          conversationId: optimisticConversationId,
          requestId: optimisticRequestId,
          error: "Scout could not start this conversation.",
        });
        this.setRuntimeSnapshot(optimisticConversationId, {
          isSending: false,
          error: normalizeError(error),
        });
        throw error;
      });

    return {
      optimisticConversationId,
      completion,
    };
  }

  // Aborts the active turn for one conversation and keeps listening long enough
  // to observe the terminal abort event from Scout.
  public async abortTurn(input: AbortTurnInput): Promise<boolean> {
    if (isOptimisticId(input.conversationId)) {
      return false;
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (activeTurn?.status === "starting") {
      return false;
    }

    this.ensureRuntime(input.conversationId, input.tenantId);
    this.setRuntimeSnapshot(input.conversationId, {
      isAborting: true,
      error: undefined,
    });

    try {
      const result = await this.apolloClient.mutate({
        mutation: AbortScoutTurnDocument,
        variables: {
          input: {
            tenantId: input.tenantId,
            conversationId: input.conversationId,
          },
        },
      });
      const abortResult = result.data?.abortTurn;
      if (!abortResult) {
        this.setRuntimeSnapshot(input.conversationId, {
          isAborting: false,
        });
        return false;
      }

      if (abortResult.success) {
        this.store.markTurnPending({
          conversationId: abortResult.conversationId,
          requestId: abortResult.requestId,
        });
        this.ensureStreamAttached({
          tenantId: input.tenantId,
          conversationId: abortResult.conversationId,
          initialLastEventId: activeTurn?.lastEventId,
          reason: "abort-turn",
        });
        void this.loadHistory({
          tenantId: input.tenantId,
          conversationId: abortResult.conversationId,
          forceNetwork: true,
          allowWhileActive: true,
          reason: "abort-recovery",
        });
      }

      this.setRuntimeSnapshot(input.conversationId, {
        isAborting: false,
      });

      return abortResult.success;
    } catch (error) {
      this.setRuntimeSnapshot(input.conversationId, {
        isAborting: false,
        error: normalizeError(error),
      });
      throw error;
    }
  }

  // Forces a persisted history refresh for one conversation.
  public async refetchConversation(input: RefetchConversationInput): Promise<void> {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    await this.loadHistory({
      tenantId: input.tenantId,
      conversationId: input.conversationId,
      forceNetwork: true,
      reason: "manual-refetch",
    });
  }

  private ensureRuntime(conversationId: string, tenantId: string): ConversationRuntime {
    const existing = this.runtimeByConversationId.get(conversationId);
    if (existing) {
      if (existing.tenantId !== tenantId) {
        existing.tenantId = tenantId;
      }
      return existing;
    }

    const runtime: ConversationRuntime = {
      ...EMPTY_CONVERSATION_RUNTIME_SNAPSHOT,
      tenantId,
      attachmentCount: 0,
      historyLoadVersion: 0,
      observer: undefined,
      observerUnsubscribe: undefined,
      initialLastEventId: undefined,
      processedEventIds: new Set<string>(),
      historyResumeEventId: undefined,
      lastRestartKey: undefined,
      lastTerminalRefetchKey: undefined,
      lastClosedActiveRefetchKey: undefined,
      hasStartedStreamConnection: false,
      pendingStreamUpdateTimeout: undefined,
    };
    this.runtimeByConversationId.set(conversationId, runtime);
    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [conversationId]: EMPTY_CONVERSATION_RUNTIME_SNAPSHOT,
      },
    });
    return runtime;
  }

  // Mirrors controller-local runtime flags into the external-store snapshot that
  // hooks subscribe to.
  private setRuntimeSnapshot(
    conversationId: string,
    nextFields: Partial<ConversationRuntimeSnapshot>
  ) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    const nextSnapshot = {
      loading: "loading" in nextFields ? (nextFields.loading ?? false) : runtime.loading,
      error: "error" in nextFields ? nextFields.error : runtime.error,
      isSending: "isSending" in nextFields ? (nextFields.isSending ?? false) : runtime.isSending,
      isAborting:
        "isAborting" in nextFields ? (nextFields.isAborting ?? false) : runtime.isAborting,
    };
    const didChange =
      runtime.loading !== nextSnapshot.loading ||
      runtime.error !== nextSnapshot.error ||
      runtime.isSending !== nextSnapshot.isSending ||
      runtime.isAborting !== nextSnapshot.isAborting;

    runtime.loading = nextSnapshot.loading;
    runtime.error = nextSnapshot.error;
    runtime.isSending = nextSnapshot.isSending;
    runtime.isAborting = nextSnapshot.isAborting;

    if (!didChange) {
      return;
    }

    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [conversationId]: nextSnapshot,
      },
    });
  }

  private deleteRuntime(conversationId: string, options?: { preserveAlias?: boolean }) {
    const didDelete = this.runtimeByConversationId.delete(conversationId);
    if (!didDelete) {
      return;
    }

    const { [conversationId]: _deleted, ...remainingRuntimes } =
      this.snapshot.runtimesByConversationId;
    this.setControllerSnapshot({
      runtimesByConversationId: remainingRuntimes,
      reconciledConversationIdsByConversationId: options?.preserveAlias
        ? this.snapshot.reconciledConversationIdsByConversationId
        : Object.fromEntries(
            Object.entries(this.snapshot.reconciledConversationIdsByConversationId).filter(
              ([aliasConversationId]) => aliasConversationId !== conversationId
            )
          ),
    });
  }

  private maybeDeleteRuntime(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime || runtime.attachmentCount > 0 || runtime.observer) {
      return;
    }

    const snapshot = this.store.getSnapshot();
    const hasConversationData =
      snapshot.conversationsById[conversationId] !== undefined ||
      (snapshot.messagesByConversationId[conversationId]?.length ?? 0) > 0;

    if (
      runtime.loading ||
      runtime.isSending ||
      runtime.isAborting ||
      runtime.error ||
      hasConversationData
    ) {
      return;
    }

    this.deleteRuntime(conversationId);
  }

  // Moves controller bookkeeping from an optimistic conversation id to the real
  // persisted id once sendMessage returns from the backend.
  private transferRuntime(input: {
    fromConversationId: string;
    toConversationId: string;
    tenantId: string;
  }) {
    const fromRuntime = this.runtimeByConversationId.get(input.fromConversationId);
    if (!fromRuntime) {
      return;
    }

    const toRuntime = this.ensureRuntime(input.toConversationId, input.tenantId);
    toRuntime.attachmentCount += fromRuntime.attachmentCount;
    toRuntime.loading = fromRuntime.loading || toRuntime.loading;
    toRuntime.error = fromRuntime.error ?? toRuntime.error;
    toRuntime.isSending = fromRuntime.isSending || toRuntime.isSending;
    toRuntime.isAborting = fromRuntime.isAborting || toRuntime.isAborting;
    toRuntime.historyResumeEventId =
      fromRuntime.historyResumeEventId ?? toRuntime.historyResumeEventId;
    for (const processedEventId of fromRuntime.processedEventIds) {
      toRuntime.processedEventIds.add(processedEventId);
    }

    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [input.toConversationId]: {
          loading: toRuntime.loading,
          error: toRuntime.error,
          isSending: toRuntime.isSending,
          isAborting: toRuntime.isAborting,
        },
      },
      reconciledConversationIdsByConversationId: {
        ...this.snapshot.reconciledConversationIdsByConversationId,
        [input.fromConversationId]: input.toConversationId,
      },
    });

    if (fromRuntime.observer) {
      fromRuntime.observerUnsubscribe?.();
      fromRuntime.observer.destroy();
    }
    this.deleteRuntime(input.fromConversationId, {
      preserveAlias: true,
    });
  }

  private async loadHistory(input: {
    tenantId: string;
    conversationId: string;
    forceNetwork: boolean;
    allowWhileActive?: boolean;
    reason: LoadHistoryReason;
  }) {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (!input.allowWhileActive && isLocallyActiveTurn(activeTurn)) {
      debugScoutConversation("skip history load for active turn", {
        conversationId: input.conversationId,
        reason: input.reason,
        activeTurnStatus: activeTurn.status,
        lastEventId: activeTurn.lastEventId,
      });
      this.ensureStreamAttached({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        initialLastEventId: activeTurn.lastEventId ?? runtime.historyResumeEventId,
        reason: "load-history-active-turn",
      });
      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
      });
      return;
    }

    runtime.historyLoadVersion += 1;
    const loadVersion = runtime.historyLoadVersion;
    debugScoutConversation("load history", {
      conversationId: input.conversationId,
      reason: input.reason,
      forceNetwork: input.forceNetwork,
      allowWhileActive: Boolean(input.allowWhileActive),
      loadVersion,
    });
    this.setRuntimeSnapshot(input.conversationId, {
      loading: true,
      error: undefined,
    });

    try {
      const result = await this.apolloClient.query({
        query: GetScoutConversationHistoryDocument,
        variables: {
          tenantId: input.tenantId,
          conversationId: input.conversationId,
        },
        fetchPolicy: input.forceNetwork ? "network-only" : "cache-first",
      });
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.historyLoadVersion !== loadVersion) {
        return;
      }

      if (result.data?.getConversationHistory) {
        this.handleHistoryResult({
          tenantId: input.tenantId,
          conversationId: input.conversationId,
          history: result.data.getConversationHistory,
        });
      }

      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
      });
    } catch (error) {
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.historyLoadVersion !== loadVersion) {
        return;
      }

      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
        error: normalizeError(error),
      });
    }
  }

  // History is ingested into the store immediately; the controller then decides
  // whether that history implies "stream should still be active".
  private handleHistoryResult(input: {
    tenantId: string;
    conversationId: string;
    history: {
      conversationId: string;
      title?: string | null;
      messages: ScoutConversationMessage[];
    };
  }) {
    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];

    debugScoutConversation("ingest history", {
      conversationId: input.history.conversationId,
      messageCount: input.history.messages.length,
      activeTurnStatus: activeTurn?.status,
      hasObserver: Boolean(runtime.observer),
    });
    this.store.ingestHistory({
      tenantId: input.tenantId,
      conversationId: input.history.conversationId,
      title: input.history.title,
      messages: input.history.messages,
    });

    runtime.historyResumeEventId = getHistoryResumeEventId(input.history.messages);

    if (!shouldResumeHistoryStream(input.history.messages)) {
      if (isLocallyActiveTurn(activeTurn)) {
        this.store.markTurnFinishedFromHistory({
          conversationId: input.conversationId,
          requestId: activeTurn.requestId,
          lastEventId: runtime.historyResumeEventId,
        });
      }
      this.detachStream(input.conversationId);
      return;
    }

    if (isLocallyActiveTurn(activeTurn) && runtime.observer) {
      debugScoutConversation("keep active stream after history ingest", {
        conversationId: input.history.conversationId,
        activeTurnStatus: activeTurn.status,
        resumeEventId: runtime.historyResumeEventId,
      });
      return;
    }

    debugScoutConversation("resume stream from history", {
      conversationId: input.history.conversationId,
      resumeEventId: runtime.historyResumeEventId,
    });
    this.ensureStreamAttached({
      tenantId: input.tenantId,
      conversationId: input.history.conversationId,
      initialLastEventId: runtime.historyResumeEventId,
      reason: "history-resume",
    });
  }

  // Streams are conversation-scoped, not hook-scoped. This method makes sure the
  // right observer exists for the conversation and resume cursor we want.
  private ensureStreamAttached(input: {
    tenantId: string;
    conversationId: string;
    initialLastEventId: string | undefined;
    reason: EnsureStreamReason;
  }) {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (isTerminalTurn(activeTurn)) {
      this.detachStream(input.conversationId);
      return;
    }

    const streamUrl = getScoutStreamUrl(input.tenantId, input.conversationId);
    const currentStreamKey = runtime.observer
      ? `${streamUrl}:${runtime.initialLastEventId ?? "none"}`
      : undefined;
    const nextStreamKey = `${streamUrl}:${input.initialLastEventId ?? "none"}`;

    debugScoutConversation("ensure stream attached", {
      conversationId: input.conversationId,
      reason: input.reason,
      currentStreamKey,
      nextStreamKey,
      activeTurnStatus: activeTurn?.status,
    });

    if (runtime.observer && currentStreamKey === nextStreamKey) {
      this.handleStreamUpdate(input.conversationId);
      return;
    }

    this.detachStream(input.conversationId);
    runtime.initialLastEventId = input.initialLastEventId;
    runtime.hasStartedStreamConnection = false;
    runtime.observer = new StreamObserver(this.streamClient, {
      enabled: true,
      url: streamUrl,
      initialLastEventId: input.initialLastEventId,
      getIdTokenRefreshIfExpired: this.getIdTokenRefreshIfExpired,
    });
    runtime.observerUnsubscribe = runtime.observer.subscribe(() => {
      this.scheduleStreamUpdate(input.conversationId);
    });
    void runtime.observer.connect().catch(() => undefined);
  }

  private detachStream(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime?.observer) {
      return;
    }

    debugScoutConversation("detach stream", {
      conversationId,
      streamKey: `${getScoutStreamUrl(runtime.tenantId, conversationId)}:${
        runtime.initialLastEventId ?? "none"
      }`,
    });

    runtime.observerUnsubscribe?.();
    runtime.observerUnsubscribe = undefined;
    runtime.observer.destroy();
    runtime.observer = undefined;
    runtime.initialLastEventId = undefined;
    runtime.hasStartedStreamConnection = false;
    if (runtime.pendingStreamUpdateTimeout !== undefined) {
      clearTimeout(runtime.pendingStreamUpdateTimeout);
      runtime.pendingStreamUpdateTimeout = undefined;
    }
    this.store.setStreamStatus(conversationId, "closed");
  }

  private scheduleStreamUpdate(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime || runtime.pendingStreamUpdateTimeout !== undefined) {
      return;
    }

    runtime.pendingStreamUpdateTimeout = setTimeout(() => {
      const latestRuntime = this.runtimeByConversationId.get(conversationId);
      if (!latestRuntime) {
        return;
      }

      latestRuntime.pendingStreamUpdateTimeout = undefined;
      this.handleStreamUpdate(conversationId);
    }, 0);
  }

  private handleStreamUpdate(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime?.observer) {
      return;
    }

    const streamResult = runtime.observer.getCurrentResult();
    if (streamResult.status === "connecting" || streamResult.status === "connected") {
      runtime.hasStartedStreamConnection = true;
    }
    let sawTerminalEvent = false;
    this.store.batch(() => {
      this.store.setStreamStatus(conversationId, streamResult.status);

      for (const event of streamResult.data) {
        const processedEventKey = `${conversationId}:${event.id}`;
        if (runtime.processedEventIds.has(processedEventKey)) {
          continue;
        }

        runtime.processedEventIds.add(processedEventKey);
        debugScoutConversation("stream event", {
          conversationId,
          eventId: event.id,
          eventName: event.event,
          hasPart: Boolean(
            typeof event.data === "object" && event.data !== null && "part" in event.data
          ),
        });
        sawTerminalEvent = this.store.applyStreamEvent(event) || sawTerminalEvent;
      }
    });

    if (streamResult.error) {
      this.setRuntimeSnapshot(conversationId, {
        error: streamResult.error,
      });
    }

    if (sawTerminalEvent) {
      debugScoutConversation("terminal stream event", {
        conversationId,
        lastEventId: streamResult.lastEventId,
      });
      this.detachStream(conversationId);
      void this.maybeRefetchTerminalHistory(conversationId);
      return;
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (streamResult.status === "closed" && isLocallyActiveTurn(activeTurn)) {
      if (!runtime.hasStartedStreamConnection) {
        debugScoutConversation("ignore pre-connect closed stream snapshot", {
          conversationId,
          requestId: activeTurn.requestId,
          lastEventId: streamResult.lastEventId,
        });
        return;
      }

      const shouldRestart = this.shouldRestartStream({
        conversationId,
        requestId: activeTurn.requestId,
        lastEventId: streamResult.lastEventId,
      });
      if (shouldRestart) {
        debugScoutConversation("restart closed active stream", {
          conversationId,
          requestId: activeTurn.requestId,
          lastEventId: streamResult.lastEventId,
        });
        void runtime.observer.restart().catch(() => undefined);
        return;
      }

      void this.maybeRefetchClosedActiveTurnHistory({
        conversationId,
        requestId: activeTurn.requestId,
        lastEventId: streamResult.lastEventId,
      });
    }
  }

  private shouldRestartStream(input: {
    conversationId: string;
    requestId: string;
    lastEventId: string | undefined;
  }) {
    const runtime = this.runtimeByConversationId.get(input.conversationId);
    if (!runtime) {
      return false;
    }

    const restartKey = `${input.conversationId}:${input.requestId}:${input.lastEventId ?? "none"}`;
    if (runtime.lastRestartKey === restartKey) {
      return false;
    }

    runtime.lastRestartKey = restartKey;
    return true;
  }

  private async maybeRefetchTerminalHistory(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (!activeTurn?.lastEventId || !isTerminalTurn(activeTurn)) {
      return;
    }

    const refetchKey = `${conversationId}:${activeTurn.lastEventId}`;
    if (runtime.lastTerminalRefetchKey === refetchKey) {
      return;
    }

    runtime.lastTerminalRefetchKey = refetchKey;
    debugScoutConversation("refetch terminal history", {
      conversationId,
      lastEventId: activeTurn.lastEventId,
      status: activeTurn.status,
    });

    await this.loadHistory({
      tenantId: runtime.tenantId,
      conversationId,
      forceNetwork: true,
      reason: "terminal-refetch",
    });

    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.maybeDeleteRuntime(conversationId);
    }
  }

  private async maybeRefetchClosedActiveTurnHistory(input: {
    conversationId: string;
    requestId: string;
    lastEventId: string | undefined;
  }) {
    const runtime = this.runtimeByConversationId.get(input.conversationId);
    if (!runtime) {
      return;
    }

    const refetchKey = `${input.conversationId}:${input.requestId}:${input.lastEventId ?? "none"}`;
    if (runtime.lastClosedActiveRefetchKey === refetchKey) {
      return;
    }

    runtime.lastClosedActiveRefetchKey = refetchKey;
    debugScoutConversation("refetch closed active stream history", {
      conversationId: input.conversationId,
      requestId: input.requestId,
      lastEventId: input.lastEventId,
    });

    await this.loadHistory({
      tenantId: runtime.tenantId,
      conversationId: input.conversationId,
      forceNetwork: true,
      allowWhileActive: true,
      reason: "closed-active-refetch",
    });

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.maybeDeleteRuntime(input.conversationId);
    }
  }

  private notifyListeners() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private handleStoreUpdate = () => {
    const nextStoreSnapshot = this.store.getSnapshot();
    if (this.snapshot.storeSnapshot === nextStoreSnapshot) {
      return;
    }

    this.setControllerSnapshot({
      storeSnapshot: nextStoreSnapshot,
    });
  };

  private setControllerSnapshot(nextFields: Partial<ScoutConversationControllerSnapshot>) {
    this.snapshot = {
      storeSnapshot: nextFields.storeSnapshot ?? this.snapshot.storeSnapshot,
      runtimesByConversationId:
        nextFields.runtimesByConversationId ?? this.snapshot.runtimesByConversationId,
      reconciledConversationIdsByConversationId:
        nextFields.reconciledConversationIdsByConversationId ??
        this.snapshot.reconciledConversationIdsByConversationId,
    };
    this.notifyListeners();
  }
}
